import { makeAutoObservable } from "mobx";

class EntryUIStore {
  showCreateEntriesDialog = false;
  showNxmapImportDialog = false;
  createDialogPreset = null;
  editingEntry = null;
  editingSubEntries = null;
  deletingEntryId = null;
  // { entryIds, year, count } — populated when the user clicks "Delete All"
  // on a YearGroup. Cleared when the confirm dialog closes.
  bulkDeleteContext = null;
  proofEntry = null;
  selectedOrg = [];
  selectedYear = "all";
  searchEntry = "";
  loadingFrameworks = false;
  loadingDisclosures = false;
  loadingEntries = false;
  creatingEntries = false;
  updatingEntry = false;
  deletingEntry = false;
  downloadingTemplate = false;
  batchUploadOpen = false;
  parsingFile = false;
  confirmingUpload = false;
  parseResult = null;
  batchUploadResetVersion = 0;
  batchTemplateType = "without_data";
  batchTemplateOrganizationId = "";
  batchTemplateCategory = "";
  batchTemplateFramework = "";
  batchTemplateYear = "";
  batchTemplateYearOptions = [];
  batchTemplateOrganizationOptions = [];
  batchTemplateFrameworkOptions = [];
  batchTemplateCategoryOptions = [];
  loadingBatchTemplateYears = false;
  loadingBatchTemplateFrameworks = false;
  loadingBatchTemplateCategories = false;
  entriesVersion = 0;
  showNxmapBulkSyncDialog = false;
  mobileSidebarOpen = false;

  constructor() {
    makeAutoObservable(this);
  }

  bumpEntriesVersion = () => this.entriesVersion++;

  setShowCreateEntriesDialog = (v) => (this.showCreateEntriesDialog = v);

  setCreateDialogPreset = (v) => (this.createDialogPreset = v);

  setShowNxmapImportDialog = (v) => (this.showNxmapImportDialog = v);

  setShowNxmapBulkSyncDialog = (v) => (this.showNxmapBulkSyncDialog = v);

  setMobileSidebarOpen = (v) => (this.mobileSidebarOpen = v);

  setEditingEntry = (entry) => (this.editingEntry = entry);

  setEditingSubEntries = (data) => (this.editingSubEntries = data);

  setDeletingEntryId = (id) => (this.deletingEntryId = id);

  setBulkDeleteContext = (ctx) => (this.bulkDeleteContext = ctx);

  setProofEntry = (entry) => (this.proofEntry = entry);

  setSelectedOrg = (v) => (this.selectedOrg = v);

  setSelectedYear = (v) => (this.selectedYear = v);

  setSearchEntry = (v) => (this.searchEntry = v);

  setLoadingFrameworks = (v) => (this.loadingFrameworks = v);

  setLoadingDisclosures = (v) => (this.loadingDisclosures = v);

  setLoadingEntries = (v) => (this.loadingEntries = v);

  setCreatingEntries = (v) => (this.creatingEntries = v);

  setUpdatingEntry = (v) => (this.updatingEntry = v);

  setDeletingEntry = (v) => (this.deletingEntry = v);

  setDownloadingTemplate = (v) => (this.downloadingTemplate = v);

  resetBatchTemplateYearState = () => {
    this.batchTemplateYear = "";
    this.batchTemplateYearOptions = [];
  };

  resetBatchTemplateOrganizationState = () => {
    this.batchTemplateOrganizationId = "";
    this.batchTemplateOrganizationOptions = [];
  };

  resetBatchTemplateFrameworkState = () => {
    this.batchTemplateFramework = "";
    this.batchTemplateFrameworkOptions = [];
  };

  resetBatchTemplateCategoryState = () => {
    this.batchTemplateCategory = "";
    this.batchTemplateCategoryOptions = [];
  };

  resetBatchTemplateState = () => {
    this.batchTemplateType = "without_data";
    this.resetBatchTemplateYearState();
    this.resetBatchTemplateOrganizationState();
    this.resetBatchTemplateFrameworkState();
    this.resetBatchTemplateCategoryState();
    this.loadingBatchTemplateYears = false;
    this.loadingBatchTemplateFrameworks = false;
    this.loadingBatchTemplateCategories = false;
  };

  resetBatchUploadState = () => {
    this.batchUploadResetVersion += 1;
    this.downloadingTemplate = false;
    this.parsingFile = false;
    this.confirmingUpload = false;
    this.parseResult = null;
    this.resetBatchTemplateState();
  };

  setBatchUploadOpen = (v) => {
    this.batchUploadOpen = v;
    if (!v) this.resetBatchUploadState();
  };

  setParsingFile = (v) => (this.parsingFile = v);

  setConfirmingUpload = (v) => (this.confirmingUpload = v);

  setParseResult = (v) => (this.parseResult = v);

  setBatchTemplateType = (v) => {
    if (this.batchTemplateType === v) return;
    this.batchTemplateType = v;
    this.resetBatchTemplateYearState();
    this.resetBatchTemplateOrganizationState();
    this.resetBatchTemplateFrameworkState();
    this.resetBatchTemplateCategoryState();
  };

  setBatchTemplateOrganizationId = (v) => {
    if (this.batchTemplateOrganizationId !== v) {
      this.resetBatchTemplateFrameworkState();
      this.resetBatchTemplateCategoryState();
    }
    this.batchTemplateOrganizationId = v;
  };

  setBatchTemplateYear = (v) => {
    if (this.batchTemplateYear !== v) {
      this.resetBatchTemplateOrganizationState();
      this.resetBatchTemplateFrameworkState();
      this.resetBatchTemplateCategoryState();
    }
    this.batchTemplateYear = v;
  };

  setBatchTemplateFramework = (v) => {
    if (this.batchTemplateFramework !== v) {
      this.resetBatchTemplateCategoryState();
    }
    this.batchTemplateFramework = v;
  };

  setBatchTemplateCategory = (v) => (this.batchTemplateCategory = v);

  setBatchTemplateYearOptions = (v) => (this.batchTemplateYearOptions = v);

  setBatchTemplateOrganizationOptions = (v) =>
    (this.batchTemplateOrganizationOptions = v);

  setBatchTemplateFrameworkOptions = (v) =>
    (this.batchTemplateFrameworkOptions = v);

  setBatchTemplateCategoryOptions = (v) =>
    (this.batchTemplateCategoryOptions = v);

  setLoadingBatchTemplateYears = (v) => (this.loadingBatchTemplateYears = v);

  setLoadingBatchTemplateFrameworks = (v) =>
    (this.loadingBatchTemplateFrameworks = v);

  setLoadingBatchTemplateCategories = (v) =>
    (this.loadingBatchTemplateCategories = v);

  reset = () => {
    this.showCreateEntriesDialog = false;
    this.showNxmapImportDialog = false;
    this.createDialogPreset = null;
    this.editingEntry = null;
    this.editingSubEntries = null;
    this.deletingEntryId = null;
    this.bulkDeleteContext = null;
    this.proofEntry = null;
    this.selectedOrg = [];
    this.selectedYear = "all";
    this.searchEntry = "";
    this.loadingDisclosures = false;
    this.loadingEntries = false;
    this.creatingEntries = false;
    this.updatingEntry = false;
    this.deletingEntry = false;
    this.batchUploadOpen = false;
    this.resetBatchUploadState();
    this.entriesVersion = 0;
    this.showNxmapBulkSyncDialog = false;
    this.mobileSidebarOpen = false;
  };
}

export const entryUIStore = new EntryUIStore();
